import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

const categoryDescriptions: Record<string, { heroTitle: string; heroSub: string; desc: string }> = {
  lianai: { heroTitle: '恋爱漫画', heroSub: '甜宠浪漫 · 心动时刻', desc: '汇集韩国最受欢迎恋爱漫画，从校园纯爱到都市浪漫，每一部作品都让人心动不已。' },
  qihuan: { heroTitle: '奇幻漫画', heroSub: '魔法冒险 · 异世征程', desc: '探索韩国奇幻漫画的精彩世界，龙与魔法、异世界冒险，带您进入无限的想象空间。' },
  dongzuo: { heroTitle: '动作漫画', heroSub: '热血激战 · 巅峰对决', desc: '韩国动作漫画精选，华丽的战斗场面与扣人心弦的剧情，让您感受最燃的阅读体验。' },
  xuanyi: { heroTitle: '悬疑漫画', heroSub: '烧脑推理 · 反转不断', desc: '韩国悬疑漫画专区，每一页都暗藏悬念，每一个反转都让人拍案叫绝。' },
  richang: { heroTitle: '日常漫画', heroSub: '温馨治愈 · 生活百态', desc: '韩国日常漫画精选，温暖人心的生活故事，治愈系的轻松阅读体验。' },
  gaoxiao: { heroTitle: '搞笑漫画', heroSub: '爆笑来袭 · 欢乐无限', desc: '韩国搞笑漫画大全，无厘头的幽默与夸张的剧情，解压放松的最佳选择。' },
};

const categoryBanners: Record<string, string> = {
  lianai: 'https://readdy.ai/api/search-image?query=Soft%20romantic%20watercolor%20illustration%20with%20pink%20cherry%20blossoms%20floating%20in%20a%20dreamy%20atmosphere%2C%20warm%20coral%20and%20cream%20gradient%20background%2C%20abstract%20heart%20shapes%20subtly%20blended%2C%20elegant%20minimalist%20style%2C%20no%20text&width=1920&height=700&seq=cat-banner-love&orientation=landscape',
  qihuan: 'https://readdy.ai/api/search-image?query=Magical%20fantasy%20watercolor%20illustration%20with%20floating%20islands%20silhouettes%20and%20stardust%2C%20deep%20purple%20and%20gold%20gradient%20background%2C%20mystical%20glowing%20particles%2C%20ethereal%20atmospheric%20mood%2C%20no%20text&width=1920&height=700&seq=cat-banner-fantasy&orientation=landscape',
  dongzuo: 'https://readdy.ai/api/search-image?query=Dynamic%20action%20ink%20wash%20illustration%20with%20bold%20brush%20strokes%2C%20dramatic%20red%20and%20orange%20gradient%20background%2C%20energy%20explosion%20effects%2C%20powerful%20artistic%20composition%2C%20no%20text&width=1920&height=700&seq=cat-banner-action&orientation=landscape',
  xuanyi: 'https://readdy.ai/api/search-image?query=Dark%20mysterious%20watercolor%20illustration%20with%20foggy%20alley%20and%20silhouette%2C%20deep%20blue%20and%20gray%20gradient%2C%20subtle%20question%20mark%20shapes%2C%20ominous%20yet%20artistic%20atmosphere%2C%20no%20text&width=1920&height=700&seq=cat-banner-mystery&orientation=landscape',
  richang: 'https://readdy.ai/api/search-image?query=Warm%20cozy%20watercolor%20illustration%20of%20a%20cafe%20interior%20with%20soft%20sunlight%2C%20yellow%20and%20cream%20gradient%2C%20peaceful%20atmospheric%20mood%2C%20minimalist%20style%2C%20no%20text&width=1920&height=700&seq=cat-banner-daily&orientation=landscape',
  gaoxiao: 'https://readdy.ai/api/search-image?query=Bright%20colorful%20playful%20watercolor%20illustration%20with%20splash%20effects%2C%20vibrant%20orange%20and%20yellow%20gradient%2C%20cheerful%20dynamic%20composition%2C%20comic%20style%20accents%2C%20no%20text&width=1920&height=700&seq=cat-banner-comedy&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const info = categoryDescriptions[slug || ''] || categoryDescriptions.lianai;
  const category = categories.find((c) => c.slug === slug) || categories[0];
  const categoryComics = comics.filter((c) => c.category === category.name);
  const banner = categoryBanners[slug || ''] || categoryBanners.lianai;

  const pageUrl = `http://heniukeji.com/category/${slug}`;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${info.heroTitle}`, href: undefined },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://heniukeji.com/' },
      { '@type': 'ListItem', position: 2, name: `${info.heroTitle}`, item: pageUrl },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${info.heroTitle} - 哈哈漫画网`,
    description: info.desc,
    url: pageUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: '哈哈漫画网',
    },
  };

  return (
    <>
      <SEOHead
        title={`${info.heroTitle} - 高清韩漫下拉式阅读 - 哈哈漫画网`}
        description={info.desc}
        keywords={`${info.heroTitle}, 韩国漫画, 免费在线观看, 高清韩漫, 下拉式阅读, 哈哈漫画网`}
        canonicalUrl={pageUrl}
        ogTitle={`${info.heroTitle} - 哈哈漫画网`}
        ogDescription={info.desc}
        schemaJson={[collectionSchema, breadcrumbSchema]}
      />

      <section className="relative h-[380px] md:h-[480px] w-full overflow-hidden">
        <img
          src={banner}
          alt={`哈哈漫画网 - ${info.heroTitle}`}
          title={`哈哈漫画网 - ${info.heroTitle}`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55"></div>
        <Navbar />
        <div className="relative z-10 w-full h-full flex items-end pb-10 md:pb-14 px-4 md:px-6">
          <div className="w-full max-w-2xl">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <span className="inline-block px-4 py-1.5 rounded-full border border-background-50/40 text-background-50 text-xs font-medium whitespace-nowrap">
              {info.heroSub}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 mt-3">
              {info.heroTitle}
            </h1>
            <p className="text-sm md:text-base text-background-50/80 mt-2 max-w-lg">
              {info.desc}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-background-50/70 text-sm">
                共 {category.count} 部作品
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {categoryComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.slug}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl aspect-[3/4] mb-3">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`哈哈漫画网 - ${comic.title}`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-accent-500/90 text-background-50'
                        : 'bg-foreground-500/80 text-background-100'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                  <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-background-950/70 backdrop-blur-sm rounded-full px-2.5 py-1">
                    <i className="ri-star-fill text-yellow-400 text-xs"></i>
                    <span className="text-background-50 text-xs font-medium">{comic.rating}</span>
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-background-50 group-hover:text-primary-300 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <div className="flex items-center gap-2 mt-1 text-xs text-background-400">
                  <span>{comic.author}</span>
                  <span className="w-1 h-1 rounded-full bg-background-500"></span>
                  <span>共{comic.chapters}话</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {categoryComics.filter((c) => c.featured).length > 0 && (
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 mb-3">
                <i className="ri-star-fill text-xs"></i>
                编辑精选
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                {info.heroTitle} <span className="text-accent-500">精选推荐</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categoryComics.filter((c) => c.featured).slice(0, 4).map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-2xl aspect-[3/4] mb-3">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`哈哈漫画网 - ${comic.title}`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <span className="text-background-50 text-xs font-medium bg-primary-500/90 rounded-full px-3 py-1 whitespace-nowrap">立即阅读</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}